import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_ADMIN_APPLICATION_ID,
  isAdminApplicationId,
  getAdminApplication,
  resolveAdminApplicationIdFromPath,
  switchAdminApplicationPath,
  type AdminApplication,
  type AdminApplicationId,
} from './adminApplications';

const STORAGE_KEY = 'admin.selectedApplicationId';

type AdminApplicationScopeValue = {
  application: AdminApplication;
  applicationId: AdminApplicationId;
  selectApplication: (applicationId: AdminApplicationId) => void;
};

const AdminApplicationScopeContext = createContext<AdminApplicationScopeValue | null>(null);

function readStoredApplicationId(): AdminApplicationId {
  if (typeof window === 'undefined') return DEFAULT_ADMIN_APPLICATION_ID;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isAdminApplicationId(stored) ? stored : DEFAULT_ADMIN_APPLICATION_ID;
}

export function AdminApplicationScopeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const applicationId = resolveAdminApplicationIdFromPath(location.pathname) ?? readStoredApplicationId();

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, applicationId);
  }, [applicationId]);

  const value = useMemo<AdminApplicationScopeValue>(() => ({
    application: getAdminApplication(applicationId),
    applicationId,
    selectApplication: (nextApplicationId) => {
      navigate(switchAdminApplicationPath(location.pathname, nextApplicationId));
    },
  }), [applicationId, location.pathname, navigate]);

  return (
    <AdminApplicationScopeContext.Provider value={value}>
      {children}
    </AdminApplicationScopeContext.Provider>
  );
}

export function useAdminApplicationScope(): AdminApplicationScopeValue {
  const value = useContext(AdminApplicationScopeContext);
  if (!value) {
    throw new Error('useAdminApplicationScope must be used inside AdminApplicationScopeProvider');
  }
  return value;
}
